// Shared pieces for the Claude 對話 inside 每日看板 (OwnerBoardPage + AgentThreadPage).
// Backend: /api/agent-threads (owner-only). See backend services/agentTasks.service.js.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import api from '../../services/api';

// ── types ────────────────────────────────────────────────────────────────────

export type ThreadStatus = 'idle' | 'queued' | 'running' | 'awaiting_approval' | 'failed';

export interface AgentThread {
  id: number;
  title: string;
  workstream: string | null;
  preset_key: string | null;
  model: string;
  effort: string;
  status: ThreadStatus;
  status_line: string | null;
  notify_done: boolean;
  pinned: boolean;
  archived?: boolean;
  run_started_at: string | null;
  created_at: string;
  updated_at: string;
  preview?: string | null;
  preview_role?: string | null;
}

export interface ApprovalMeta {
  summary: string;
  details: string;
  impact?: string | null;
  options?: string[];
  images?: string[];
  state: 'open' | 'approved' | 'answered' | 'superseded' | 'done_by_owner';
  answer?: string | null;
}

export interface AgentMessage {
  id: number;
  role: 'user' | 'assistant' | 'activity' | 'approval' | 'system';
  content: string;
  meta: Record<string, any>;
  created_at: string;
}

export interface Decision {
  id: number;
  thread_id: number;
  content: string;
  meta: ApprovalMeta;
  created_at: string;
  thread_title: string;
  workstream: string | null;
}

export interface Runner {
  hostname: string | null;
  last_seen_at: string;
  online: boolean;
  auth_ok: boolean | null;
  auth_detail: string | null;
  running_ids: number[];
}

export interface AgentPreset {
  key: string;
  label: string;
  workstream: string;
  needsInput: boolean;
  placeholder: string | null;
}

export interface AgentConfig {
  presets: AgentPreset[];
  models: string[];
  efforts: string[];
  defaults: { model: string; effort: string };
}

// ── helpers ──────────────────────────────────────────────────────────────────

export const errMessage = (err: any): string =>
  err?.response?.data?.message || err?.message || '發生錯誤';

// zh-TW short date (e.g. 7/26); null when missing/invalid.
export const shortDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric', timeZone: 'Asia/Taipei' });
};

// "3分" / "2小時" / "10/9" — compact age for one-line rows.
export const ago = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return '剛剛';
  if (min < 60) return `${min}分`;
  if (min < 24 * 60) return `${Math.round(min / 60)}小時`;
  return shortDate(iso) || '';
};

export const clockTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Taipei' });
};

export const MODEL_LABEL: Record<string, string> = {
  opus: 'Opus',
  sonnet: 'Sonnet',
  haiku: 'Haiku',
  fable: 'Fable',
};

export const modelBadge = (model?: string | null, effort?: string | null) =>
  `${MODEL_LABEL[model || ''] || model || ''}${effort ? ` · ${effort}` : ''}`;

const WORKSTREAM_COLOR: Record<string, { bg: string; fg: string }> = {
  廣告: { bg: '#EEEDFE', fg: '#3C3489' },
  上架: { bg: '#E1F5EE', fg: '#085041' },
  穿搭: { bg: '#FAECE7', fg: '#712B13' },
  內容: { bg: '#FBEAF0', fg: '#72243E' },
  客服: { bg: '#E6F1FB', fg: '#0C447C' },
  NOKE: { bg: '#FAEEDA', fg: '#633806' },
  MdoC: { bg: '#EAF3DE', fg: '#27500A' },
  營運: { bg: '#F1EFE8', fg: '#444441' },
  // AI employees (2026-11-04) — the five areas that had no workstream before.
  官網: { bg: '#E8F0F7', fg: '#1F4A6E' },
  回購: { bg: '#F5EAF6', fg: '#5E2A63' },
  採購: { bg: '#EEF2E2', fg: '#3F4A16' },
  財務: { bg: '#F7F0DC', fg: '#5A4507' },
  工程: { bg: '#E9EBEE', fg: '#2E3440' },
};

export const WorkstreamTag: React.FC<{ name: string | null }> = ({ name }) => {
  if (!name) return null;
  const c = WORKSTREAM_COLOR[name] || WORKSTREAM_COLOR['營運'];
  return (
    <Box
      component="span"
      sx={{ bgcolor: c.bg, color: c.fg, fontSize: 11, px: 1, py: 0.25, borderRadius: 1, fontWeight: 600, flexShrink: 0 }}
    >
      {name}
    </Box>
  );
};

// Minimal markdown for Claude's replies: **bold**, `code`, and line breaks.
export const RichText: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`\n]+`)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith('`') && p.endsWith('`') && p.length > 2)
          return (
            <Box key={i} component="code" sx={{ fontSize: '0.9em', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>
              {p.slice(1, -1)}
            </Box>
          );
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </>
  );
};

export const LogBox: React.FC<{ text: string; maxHeight?: number }> = ({ text, maxHeight = 280 }) => {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [text]);
  return (
    <Box
      component="pre"
      ref={ref}
      sx={{
        m: 0,
        mt: 1,
        p: 1.25,
        maxHeight,
        overflow: 'auto',
        bgcolor: 'grey.900',
        color: 'grey.100',
        borderRadius: 1,
        fontSize: 11.5,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text || '…'}
    </Box>
  );
};

let configCache: AgentConfig | null = null;
export const useAgentConfig = (): AgentConfig | null => {
  const [config, setConfig] = useState<AgentConfig | null>(configCache);
  useEffect(() => {
    if (configCache) return;
    api
      .get('/api/agent-threads/config')
      .then((res) => {
        configCache = res.data;
        setConfig(res.data);
      })
      .catch(() => setConfig(null));
  }, []);
  return config;
};

// Downscale a picked photo to a JPEG data URI (phone photos are 4–8MB).
const fileToDataUri = (file: File, maxEdge = 1600): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('讀取圖片失敗'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('不是圖片'));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

// ── composer ─────────────────────────────────────────────────────────────────

export interface ComposerSubmit {
  text: string;
  model: string;
  effort: string;
  images: string[];
}

export const Composer: React.FC<{
  config: AgentConfig | null;
  model: string;
  effort: string;
  onModelChange: (m: string) => void;
  onEffortChange: (e: string) => void;
  onSubmit: (v: ComposerSubmit) => Promise<void>;
  placeholder: string;
  sendLabel?: string;
  text?: string;
  onTextChange?: (t: string) => void;
  extraLeft?: React.ReactNode;
  autoFocus?: boolean;
}> = ({
  config,
  model,
  effort,
  onModelChange,
  onEffortChange,
  onSubmit,
  placeholder,
  sendLabel,
  text: controlledText,
  onTextChange,
  extraLeft,
  autoFocus,
}) => {
  const [ownText, setOwnText] = useState('');
  const text = controlledText ?? ownText;
  const setText = onTextChange ?? setOwnText;
  const [images, setImages] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = async (files: FileList | null) => {
    if (!files) return;
    const next = [...images];
    for (const f of Array.from(files).slice(0, 4 - next.length)) {
      try {
        next.push(await fileToDataUri(f));
      } catch {
        /* skip unreadable files */
      }
    }
    setImages(next);
  };

  const submit = async () => {
    if (sending || (!text.trim() && images.length === 0)) return;
    setSending(true);
    try {
      await onSubmit({ text: text.trim(), model, effort, images });
      setText('');
      setImages([]);
    } finally {
      setSending(false);
    }
  };

  const selectSx = { fontSize: 12, height: 28, '& .MuiSelect-select': { py: 0.25, pl: 1.25 } };

  return (
    <Paper elevation={6} sx={{ p: 1.25, borderRadius: 4 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Select size="small" value={model} onChange={(e) => onModelChange(String(e.target.value))} sx={selectSx}>
          {(config?.models || ['opus', 'sonnet', 'haiku', 'fable']).map((m) => (
            <MenuItem key={m} value={m}>
              {MODEL_LABEL[m] || m}
            </MenuItem>
          ))}
        </Select>
        <Select size="small" value={effort} onChange={(e) => onEffortChange(String(e.target.value))} sx={selectSx}>
          {(config?.efforts || ['low', 'medium', 'high', 'xhigh', 'max']).map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
        </Select>
        {extraLeft}
      </Stack>
      {images.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
          {images.map((src, i) => (
            <Box key={i} sx={{ position: 'relative' }}>
              <Box component="img" src={src} alt="" sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }} />
              <IconButton
                size="small"
                aria-label="移除圖片"
                onClick={() => setImages((im) => im.filter((_, j) => j !== i))}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: 0.25 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={0.5} alignItems="flex-end">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
        <Tooltip title="附圖片">
          <IconButton aria-label="附圖片" onClick={() => fileRef.current?.click()} disabled={images.length >= 4}>
            <AddPhotoAlternateOutlinedIcon />
          </IconButton>
        </Tooltip>
        <TextField
          fullWidth
          multiline
          maxRows={8}
          size="small"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        {sendLabel ? (
          <Button
            variant="contained"
            disableElevation
            disabled={sending || (!text.trim() && images.length === 0)}
            onClick={submit}
            sx={{ whiteSpace: 'nowrap', borderRadius: 3 }}
          >
            {sending ? <CircularProgress size={18} /> : sendLabel}
          </Button>
        ) : (
          <IconButton
            color="primary"
            aria-label="送出"
            disabled={sending || (!text.trim() && images.length === 0)}
            onClick={submit}
          >
            {sending ? <CircularProgress size={20} /> : <SendRoundedIcon />}
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
};

// ── approval card ────────────────────────────────────────────────────────────

export const ApprovalCard: React.FC<{
  meta: ApprovalMeta;
  header?: React.ReactNode;
  busy?: boolean;
  onApprove?: (note?: string) => void;
  onAnswer?: (answer: string, note?: string) => void;
  onRevise?: (note: string) => void;
  /** 「我自己做了」 — he already did it: closes the card, nothing runs. */
  onDone?: () => void;
  onOpen?: () => void;
  onZoom?: (src: string) => void;
}> = ({ meta, header, busy, onApprove, onAnswer, onRevise, onDone, onOpen, onZoom }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [showAll, setShowAll] = useState(false);
  const options = meta.options || [];
  const open = meta.state === 'open';
  const longDetails = (meta.details || '').split('\n').length > 4 || (meta.details || '').length > 220;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.75, borderRadius: 3, borderColor: open ? 'warning.main' : 'divider', opacity: open ? 1 : 0.75 }}
    >
      {header}
      {/* A long impact chip wraps under the title instead of squeezing it into a sliver on a phone. */}
      <Stack direction="row" useFlexGap spacing={1} alignItems="flex-start" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
        <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: 'break-word', flex: '1 1 220px', minWidth: 0 }}>
          {meta.summary}
        </Typography>
        {meta.impact && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={meta.impact}
            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
          />
        )}
      </Stack>
      {meta.details && (
        <>
          <Collapse in={!longDetails || showAll} collapsedSize={longDetails ? 64 : 0}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.75 }}>
              <RichText text={meta.details} />
            </Typography>
          </Collapse>
          {longDetails && (
            <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={() => setShowAll((v) => !v)}>
              {showAll ? '收起' : '看完整內容'}
            </Button>
          )}
        </>
      )}
      {!!meta.images?.length && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75, mt: 1.25 }}>
          {meta.images.map((src, i) => (
            <Box
              key={i}
              component="img"
              src={src}
              alt={`preview ${i + 1}`}
              onClick={() => onZoom?.(src)}
              sx={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', border: 1, borderColor: 'divider' }}
            />
          ))}
        </Box>
      )}
      {!open && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {meta.state === 'approved'
            ? '✅ 已核准'
            : meta.state === 'answered'
            ? `💬 已回答：${meta.answer}`
            : meta.state === 'done_by_owner'
            ? '✔ 你自己做了'
            : '已在對話中回覆'}
        </Typography>
      )}
      {open && (
        <>
          {noteOpen && (
            <TextField
              size="small"
              fullWidth
              multiline
              autoFocus
              maxRows={4}
              sx={{ mt: 1.25 }}
              placeholder={options.length ? '其他答案或補充' : '要怎麼改？'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
          {options.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, mt: 1.25 }}>
              {options.map((o) => (
                <Button key={o} variant="outlined" disabled={busy} onClick={() => onAnswer?.(o, note.trim() || undefined)} sx={{ py: 1 }}>
                  {o}
                </Button>
              ))}
            </Box>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: options.length ? 0.75 : 1.25 }}>
            {options.length === 0 && onApprove && (
              <Button
                variant="contained"
                color="success"
                disableElevation
                disabled={busy}
                onClick={() => onApprove(note.trim() || undefined)}
                sx={{ flex: 1 }}
              >
                核准
              </Button>
            )}
            {onRevise &&
              (noteOpen ? (
                <Button variant="outlined" disabled={busy || !note.trim()} onClick={() => onRevise(note.trim())} sx={{ flex: 1 }}>
                  送出
                </Button>
              ) : (
                <Button
                  variant={options.length ? 'text' : 'outlined'}
                  color="inherit"
                  size={options.length ? 'small' : 'medium'}
                  onClick={() => setNoteOpen(true)}
                  sx={{ flex: options.length ? 0 : 1, whiteSpace: 'nowrap' }}
                >
                  {options.length ? '其他答案…' : '要改…'}
                </Button>
              ))}
            {onDone && (
              <Button
                variant="outlined"
                color="inherit"
                disabled={busy}
                onClick={onDone}
                sx={{ whiteSpace: 'nowrap', flex: options.length ? 0 : undefined }}
              >
                我自己做了
              </Button>
            )}
            {onOpen && (
              <Button color="inherit" size="small" onClick={onOpen} sx={{ whiteSpace: 'nowrap', ml: 'auto' }}>
                打開對話
              </Button>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};
